/**
 * GenBank / EMBL の位置記述を 0-based 半開へ直す。
 * `complement(join(2000..2500,1..100))` のような環状ラップは
 * `end > genomeLength` の表現に畳む（§3.3）。
 */

import { Interval } from '../sequence/interval';
import { Strand } from '../annotation/types';

export interface ParsedLocation {
  start: number;
  end: number;
  strand: Strand;
  exons?: Interval[];
  /** `<` / `>` が付いていた（部分配列） */
  partial: boolean;
}

const ELEMENT_PATTERN = /^(<?)(\d+)(?:([.^]{1,2})(>?)(\d+))?$/;

/** カンマ区切りをトップレベルの括弧だけ見て分ける */
function splitTopLevel(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '(') depth += 1;
    else if (char === ')') depth -= 1;
    else if (char === ',' && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
}

interface RawSpan {
  start: number;
  end: number;
  partial: boolean;
}

function parseElements(text: string): { spans: RawSpan[]; complement: boolean } | null {
  const trimmed = text.replace(/\s+/g, '');
  if (trimmed.length === 0) return null;

  if (trimmed.startsWith('complement(') && trimmed.endsWith(')')) {
    const inner = parseElements(trimmed.slice('complement('.length, -1));
    if (!inner) return null;
    return { spans: inner.spans, complement: !inner.complement };
  }

  for (const keyword of ['join(', 'order(', 'bond(']) {
    if (trimmed.startsWith(keyword) && trimmed.endsWith(')')) {
      const inner = splitTopLevel(trimmed.slice(keyword.length, -1));
      const spans: RawSpan[] = [];
      let complement = false;
      for (const part of inner) {
        const parsed = parseElements(part);
        if (!parsed) return null;
        // 部分ごとの complement は全体の向きに寄せる（混在は稀）
        complement = complement || parsed.complement;
        spans.push(...parsed.spans);
      }
      return spans.length > 0 ? { spans, complement } : null;
    }
  }

  // 他レコード参照（`J00194.1:100..202`）は取り込まない
  if (trimmed.includes(':')) return null;

  const match = ELEMENT_PATTERN.exec(trimmed);
  if (!match) return null;

  const partial = match[1] === '<' || match[4] === '>';
  const first = Number(match[2]);
  if (!Number.isFinite(first) || first < 1) return null;

  if (match[5] === undefined) {
    // 単一塩基。`123^124`（塩基間）は 123 の 1 塩基として扱う
    return { spans: [{ start: first - 1, end: first, partial }], complement: false };
  }

  const second = Number(match[5]);
  if (!Number.isFinite(second) || second < 1) return null;
  // 環状の `100..1` は end < start のまま返し、parseGenBankLocation で畳む
  return { spans: [{ start: first - 1, end: second, partial }], complement: false };
}

export function parseGenBankLocation(
  text: string,
  genomeLength: number,
  topology: 'linear' | 'circular' = 'linear'
): ParsedLocation | null {
  const parsed = parseElements(text);
  if (!parsed || parsed.spans.length === 0) return null;

  const spans = parsed.spans;
  const strand: Strand = parsed.complement ? -1 : 1;
  const partial = spans.some((span) => span.partial);
  let exons: Interval[] = spans.map((span) => ({ start: span.start, end: span.end }));

  // 単一要素の `100..1`（環状）を尾＋頭の 2 エクソンへ展開
  if (
    exons.length === 1 &&
    exons[0].end > 0 &&
    exons[0].end <= exons[0].start &&
    topology === 'circular' &&
    genomeLength > 0
  ) {
    const headEnd = exons[0].end; // 1-based inclusive second がそのまま 0-based half-open end
    const tailStart = exons[0].start;
    exons = [
      { start: tailStart, end: genomeLength },
      { start: 0, end: headEnd },
    ];
  }

  // 各エクソンの end < start は不正（上で展開済みのはず）
  for (const exon of exons) {
    if (exon.end <= exon.start) return null;
  }

  const start = exons[0].start;
  let end = exons[exons.length - 1].end;

  // 記載順が原点をまたいで戻っていれば環状ラップ（join(2000..2500,1..100)）
  const wraps = exons.some((exon, index) => index > 0 && exon.start < exons[index - 1].start);
  if (wraps) {
    if (topology !== 'circular' || genomeLength <= 0) {
      // 線形で並びが逆転しているものは範囲だけ取る
      const min = Math.min(...exons.map((exon) => exon.start));
      const max = Math.max(...exons.map((exon) => exon.end));
      return { start: min, end: max, strand, exons, partial };
    }
    end = exons[exons.length - 1].end + genomeLength;
  }

  return {
    start,
    end,
    strand,
    exons: exons.length > 1 ? exons : undefined,
    partial,
  };
}
